import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button/Button';
import Input from './Input/Input';
import Select from './Select/Select';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableRow({ id, children, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#e2e6ea' : undefined,
    userSelect: isDragging ? 'none' : undefined,
    cursor: 'grab',
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners} {...props}>
      {children}
    </tr>
  );
}

function Levels({ levels, reloadAll }) {
  const [editingLevel, setEditingLevel] = useState(null);
  const [localLevels, setLocalLevels] = useState(levels);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');

  // Load departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await api.getDepartments();
        setDepartments(res.data);
        if (res.data.length > 0) {
          setSelectedDepartmentId(res.data[0].id);
        }
      } catch (error) {
        console.error('Error loading departments:', error);
      }
    };
    
    loadDepartments();
  }, []);

  // Update local levels when levels or selectedDepartmentId changes
  useEffect(() => {
    if (selectedDepartmentId) {
      const filtered = levels.filter(level => level.department_id === parseInt(selectedDepartmentId));
      setLocalLevels(filtered);
    } else {
      setLocalLevels(levels);
    }
  }, [levels, selectedDepartmentId]);

  const handleUpdateLevel = async (e) => {
    e.preventDefault();
    try {
      await api.updateLevel(editingLevel.id, editingLevel);
      setEditingLevel(null);
      reloadAll();
    } catch (error) {
      console.error('Error updating level:', error);
    }
  };

  const handleDeleteLevel = async (id) => {
    if (window.confirm('Are you sure you want to delete this level?')) {
      try {
        await api.deleteLevel(id);
        reloadAll();
      } catch (error) {
        console.error('Error deleting level:', error);
      }
    }
  };

  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  // dnd-kit setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = localLevels.findIndex(l => String(l.id) === String(active.id));
    const newIndex = localLevels.findIndex(l => String(l.id) === String(over.id));
    
    if (oldIndex === -1 || newIndex === -1) return; // Safety check
    
    const reordered = arrayMove(localLevels, oldIndex, newIndex);
    setLocalLevels(reordered);
    
    try {
      // Ensure we're sending numeric IDs in the correct format
      const orderIds = reordered.map(level => Number(level.id));
      
      console.log('Sending order update:', {
        order: orderIds
      });
      
      // The backend expects just an array of IDs in the 'order' property
      await api.updateLevelsOrder({
        order: orderIds
      });
      // Success: do not reloadAll, keep localLevels as is
    } catch (error) {
      console.error('Error updating order:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
      }
      reloadAll(); // Only reload if there was an error
    }
  };

  return (
    <div>
      <div className="form-group mb-4">
        <label>Filter by Department</label>
        <Select
          options={departmentOptions}
          showSearch={false}
          value={selectedDepartmentId}
          onChange={(value) => setSelectedDepartmentId(value)}
          placeholder="Select a department"
          id="department-filter"
        />
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="table-scroll-wrapper table-scroll-wrapper--inline">
      <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Department</th>
              <th>Actions</th>
            </tr>
          </thead>
          <SortableContext
            items={localLevels.map(l => String(l.id))}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {localLevels.map((level) => (
                <SortableRow key={level.id} id={String(level.id)}>
                  <td>
                    {editingLevel?.id === level.id ? (
                      <Input
                        type="text"
                        value={editingLevel.title}
                        onChange={(e) => setEditingLevel({ ...editingLevel, title: e.target.value })}
                        id={`edit-level-title-${level.id}`}
                      />
                    ) : (
                      level.title
                    )}
                  </td>
                  <td>
                    {editingLevel?.id === level.id ? (
                      <Select
                        options={departmentOptions}
                        value={editingLevel.department_id || ''}
                        onChange={(value) => setEditingLevel({ ...editingLevel, department_id: value })}
                        placeholder="Select a department"
                        id={`edit-level-department-${level.id}`}
                      />
                    ) : (
                      departments.find(d => d.id === level.department_id)?.name || '-'
                    )}
                  </td>
                  <td className="td--actions">
                    {editingLevel?.id === level.id ? (
                      <>
                        <Button type="primary" onClick={handleUpdateLevel}>Save</Button>
                        <Button type="secondary" onClick={() => setEditingLevel(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button type="secondary" onClick={() => setEditingLevel(level)}>Edit</Button>
                        <Button type="destructive" onClick={() => handleDeleteLevel(level.id)}>Delete</Button>
                      </>
                    )}
                  </td>
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
        </table>
        </div>
      </DndContext>
    </div>
  );
}

export default Levels; 